import React, { useState } from 'react';
import { Search, X, CheckCircle, XCircle, Calendar, User, Mail, CreditCard } from 'lucide-react';

const ReservationVerifier = ({ onClose, initialReservationId = null }) => {
  const [reservationId, setReservationId] = useState(initialReservationId || '');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  // Auto-verify si ID initial fourni
  React.useEffect(() => {
    if (initialReservationId) {
      handleVerify();
    }
  }, [initialReservationId]);

  const handleVerify = async () => {
    if (!reservationId.trim()) {
      setError('Veuillez entrer un numéro de réservation');
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/events/registration/verify/${reservationId.trim()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          setError('❌ Réservation introuvable');
        } else {
          setError('Erreur lors de la vérification');
        }
        return;
      }

      const data = await response.json();
      setResult(data);
    } catch (err) {
      setError('Erreur lors de la vérification');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold flex items-center gap-1">
          <CheckCircle className="w-4 h-4" /> Payé
        </span>;
      case 'pending':
        return <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-semibold">
          ⏳ En attente
        </span>;
      case 'canceled':
        return <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-semibold flex items-center gap-1">
          <XCircle className="w-4 h-4" /> Annulé
        </span>;
      default:
        return <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm">{status}</span>;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold" style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}>
              Vérifier une Réservation
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-3xl leading-none"
            >
              ×
            </button>
          </div>

          {/* Search Input */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Numéro de réservation (UUID)
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={reservationId}
                onChange={(e) => setReservationId(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleVerify()}
                placeholder="Ex: 9288a2e8-0413-4e9e-ac2d-b1cf566568dd"
                className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
              <button
                onClick={handleVerify}
                disabled={loading}
                className="px-6 py-2 text-white rounded-lg flex items-center gap-2"
                style={{ backgroundColor: 'rgb(196, 150, 96)' }}
              >
                <Search className="w-4 h-4" />
                {loading ? 'Recherche...' : 'Vérifier'}
              </button>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
              {error}
            </div>
          )}

          {/* Result */}
          {result && (
            <div className="space-y-4">
              {/* Status */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <span className="font-semibold">Statut du paiement :</span>
                {getStatusBadge(result.payment_status)}
              </div>

              {/* Event Info */}
              <div className="p-4 border rounded-lg">
                <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ color: 'rgb(196, 150, 96)' }}>
                  <Calendar className="w-5 h-5" />
                  Événement
                </h3>
                <p className="text-lg font-semibold">{result.event_title || 'N/A'}</p>
              </div>

              {/* Buyer Info */}
              <div className="p-4 border rounded-lg">
                <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ color: 'rgb(196, 150, 96)' }}>
                  <User className="w-5 h-5" />
                  Acheteur
                </h3>
                <div className="space-y-2">
                  <p><strong>Nom :</strong> {result.buyer_name}</p>
                  <p className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    <strong>Email :</strong> {result.buyer_email}
                  </p>
                </div>
              </div>

              {/* Tickets */}
              {result.tariffs_details && result.tariffs_details.length > 0 && (
                <div className="p-4 border rounded-lg">
                  <h3 className="font-semibold mb-3" style={{ color: 'rgb(196, 150, 96)' }}>
                    🎫 Billets réservés
                  </h3>
                  <div className="space-y-2">
                    {result.tariffs_details.map((td, idx) => (
                      <div key={idx} className="flex justify-between">
                        <span>{td.quantity}x {td.label}</span>
                        <span className="font-semibold">{td.subtotal?.toFixed(2) || (td.quantity * td.price).toFixed(2)} €</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Payment */}
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="font-semibold flex items-center gap-2">
                    <CreditCard className="w-5 h-5" />
                    Montant total :
                  </span>
                  <span className="text-2xl font-bold" style={{ color: 'rgb(196, 150, 96)' }}>
                    {result.amount_total?.toFixed(2) || '0.00'} €
                  </span>
                </div>
                <div className="mt-2 text-sm text-gray-600">
                  Méthode : {result.payment_method || 'N/A'}
                </div>
              </div>

              {/* Reservation ID */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Numéro de réservation</p>
                <p className="font-mono text-sm break-all" style={{ color: 'rgb(196, 150, 96)' }}>
                  {result.id}
                </p>
              </div>

              {/* Action Button */}
              {result.payment_status === 'paid' && (
                <div className="pt-4">
                  <button
                    className="w-full py-3 text-white rounded-lg text-lg font-semibold"
                    style={{ backgroundColor: 'rgb(34, 197, 94)' }}
                  >
                    ✓ Réservation Valide - Autoriser l'entrée
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReservationVerifier;
